import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import {
  ContentType,
  guessProfileFromMime,
  getMostSuitableContentType,
  getMimeWithCodecs,
} from '../../../constants/mediaFormatProfiles';
import { getStore } from '../../store';
import {
  uploadChunks,
  finalizeUpload,
  backupChunkThunk,
  stopRecordingThunk,
} from '../../thunk'; // Import the Thunks
import { cleanup, streams, mergeStreams } from './streamUtils'; // Import the updated mergeStreams
import logger from '../../../lib/logger';
import MatroskaCueGenerator from './MatroskaCueGenerator';
import Mediasoup from './webrtc/mediasoup'; // Import Mediasoup class directly

type AppDispatch = ThunkDispatch<any, any, Action<string>>;

const MIN_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB
const TIMESLICE = 1000; // 1 second
class Recorder {
  private mediaRecorder: MediaRecorder | null = null;

  private chunks: Blob[] = [];

  private firstPart: Blob | undefined;

  private bufferSize = 0;

  private nextPartNumber = 1;

  private contentType: ContentType = getMostSuitableContentType();

  private cueGenerator = new MatroskaCueGenerator();

  private ignoreData = false;

  private mediasoup: Mediasoup;

  constructor({ contentType }: { contentType: ContentType }) {
    this.contentType = contentType;
    this.handleDataAvailable = this.handleDataAvailable.bind(this);
    this.stopRecording = this.stopRecording.bind(this);
    this.handleTrackEnded = this.handleTrackEnded.bind(this);
    this.handleError = this.handleError.bind(this);
    this.mediasoup = new Mediasoup(); // Instantiate Mediasoup
  }

  async send() {
    logger.info('Sending part', this.nextPartNumber);

    const store = getStore();
    const { dispatch }: { dispatch: AppDispatch } = store;

    const blob = new Blob(this.chunks, { type: this.contentType });
    if (!this.firstPart) {
      this.firstPart = blob;
    }
    this.chunks = [];
    this.bufferSize = 0;

    dispatch(backupChunkThunk({ blob, partNumber: this.nextPartNumber }));
    dispatch(uploadChunks({ blob, partNumber: this.nextPartNumber }));
    this.nextPartNumber += 1;

    // Do at the end of the method to avoid data races that can happen
    // because of the `await` and multiple interleaved calls to send.
    try {
      await this.cueGenerator.add(blob);
    } catch (error) {
      logger.error('Cue generator error', error);
    }
  }

  async end() {
    logger.info('Finalizing...');

    const store = getStore();
    const { dispatch }: { dispatch: AppDispatch } = store;

    try {
      const meta = await this.cueGenerator.end();
      if (this.firstPart && meta.newMetadata) {
        const firstPartStripped = this.firstPart.slice(meta.oldMetadataSize);
        const cuesAndFirstPart = new Blob(
          [meta.newMetadata, firstPartStripped],
          {
            type: this.contentType,
          }
        );
        dispatch(backupChunkThunk({ blob: cuesAndFirstPart, partNumber: 1 })); // No need to wait for this
        await dispatch(uploadChunks({ blob: cuesAndFirstPart, partNumber: 1 }));
      }
    } catch (error) {
      logger.error('Cue generator error', error);
    }

    await dispatch(finalizeUpload());
  }

  handleDataAvailable = async (event: BlobEvent) => {
    logger.log('Data available', event, this.mediaRecorder?.state);

    if (this.ignoreData) {
      logger.log('Ignoring data written after the stop event');
      return;
    }

    if (event.data.size > 0) {
      this.chunks.push(event.data);
      this.bufferSize += event.data.size;

      if (this.bufferSize >= MIN_UPLOAD_SIZE) {
        await this.send();
      }
    }
  };

  handleStop = async () => {
    logger.info('Stopped');

    this.ignoreData = true;

    await this.send();
    await this.end();

    cleanup();
    this.ignoreData = false;
  };

  handleTrackEnded = () => {
    const store = getStore();
    const { dispatch, getState } = store;
    // @ts-ignore
    if (getState().recorder.status === 'recording') {
      // Avoid multiple track endings calling stopRecording
      // @ts-ignore
      dispatch(stopRecordingThunk());
    }
  };

  handleError = (event: Event) => {
    logger.log('Recorder error:', event);
  };

  async startRecording() {
    try {
      await mergeStreams();
      const { mergedStream, userMediaStream, displayMediaStream } = streams;
      const profile = guessProfileFromMime(this.contentType);
      this.mediaRecorder = new MediaRecorder(mergedStream, {
        mimeType: getMimeWithCodecs(profile, {
          audio: true,
          video: true,
        }),
      });
      this.mediaRecorder.onerror = this.handleError;
      [
        ...userMediaStream.getTracks(),
        ...displayMediaStream.getTracks(),
      ].forEach((track) => {
        track.addEventListener('ended', this.handleTrackEnded);
      });

      // Setup Mediasoup before starting the recording
      try {
        await this.mediasoup.setup(); // Use the Mediasoup instance
      } catch (error) {
        logger.error('Error setting up Mediasoup', error);
      }

      if (this.mediaRecorder) {
        this.mediaRecorder.ondataavailable = this.handleDataAvailable;
        this.mediaRecorder.onstop = this.handleStop;
        this.mediaRecorder.start(TIMESLICE);
        this.mediasoup.addMediaStream(mergedStream);
      } else {
        throw new Error('MediaRecorder not ready');
      }
    } catch (error) {
      logger.log('Error starting recording:', error);
      throw error;
    }
  }

  async stopRecording() {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }

    // Close the Mediasoup connection
    this.mediasoup.close();
  }

  pauseRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
    }
  }

  resumeRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
    }
  }
}

export default Recorder;
